import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../services/supabase'

const AuthCallback = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [error, setError] = useState(null)
  const hasRedirected = useRef(false)

  useEffect(() => {
    // Prevent double execution
    if (hasRedirected.current) return
    
    const handleCallback = async () => {
      try {
        console.log('🔄 Processing OAuth callback...')

        // Read intended redirect from the callback URL query param.
        // (Set by signInWithGoogle via redirectTo=/auth/callback?next=...)
        const requestedNext = searchParams.get('next') || '/'
        const redirectTo = (requestedNext.startsWith('/') ? requestedNext : '/')
        console.log('📍 Redirect destination (next):', redirectTo)
        
        // Get the hash fragment from the URL (Supabase OAuth returns tokens in hash)
        const hashParams = new URLSearchParams(window.location.hash.substring(1))
        const accessToken = hashParams.get('access_token')
        const refreshToken = hashParams.get('refresh_token')
        
        // Also check for error in the URL
        const errorParam = searchParams.get('error')
        const errorDescription = searchParams.get('error_description')
        
        if (errorParam) {
          console.error('❌ OAuth error:', errorParam, errorDescription)
          setError(errorDescription || errorParam)
          return
        }
        
        if (accessToken && refreshToken) {
          console.log('🔑 Found tokens in URL, setting session...')
          const { data, error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          })
          
          if (sessionError) {
            console.error('❌ Error setting session:', sessionError)
            setError(sessionError.message)
            return
          }
          
          console.log('✅ Session set successfully:', data?.user?.email)
        } else {
          // Try to get existing session (Supabase might have already set it)
          console.log('🔍 Checking for existing session...')
          const { data: { session }, error: sessionError } = await supabase.auth.getSession()
          
          if (sessionError) {
            console.error('❌ Error getting session:', sessionError)
            setError(sessionError.message)
            return
          }
          
          if (session) {
            console.log('✅ Found existing session:', session.user?.email)
          } else {
            console.log('⚠️ No session found, waiting for auth state change...')
          }
        }
        
        // Small delay to ensure auth state is updated
        await new Promise(resolve => setTimeout(resolve, 300))
        
        // Mark as redirected to prevent double navigation
        hasRedirected.current = true
        
        console.log('🔄 Redirecting to:', redirectTo)
        navigate(redirectTo, { replace: true })
        
      } catch (err) {
        console.error('💥 OAuth callback exception:', err)
        setError(err.message || 'Failed to complete authentication')
      }
    }
    
    handleCallback()
  }, [navigate, searchParams])

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Authentication Failed</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/auth')}
            className="px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6">
      <div className="text-center">
        <div className="w-12 h-12 border-3 border-black/20 border-t-black rounded-full animate-spin mx-auto mb-4"></div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Completing Sign In...</h1>
        <p className="text-gray-600">Please wait while we complete your authentication.</p>
      </div>
    </div>
  )
}

export default AuthCallback

